import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import * as api from 'api/api'
import { DisplayStatus, ReportListItem } from 'api/types'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, IconButton, Modal, ProgressBar, Spinner, StatusBadge, TextField } from 'components/ui'
import {
  SECTION_LABELS,
  STATUS_LABELS,
  STATUS_ORDER,
  WEEK_DAYS,
  addDays,
  codeWithoutMonth,
  formatDate,
  monthBounds,
  monthKey,
  monthTitle,
  monthsBetween,
  parseIso,
  weekStart,
} from 'lib/format'
import { t } from 'lib/i18n'
import { useAction, useAsync } from 'lib/hooks'

const weekdayFromDate = (iso: string) => WEEK_DAYS[(parseIso(iso).getUTCDay() + 1) % 7]

type View = 'table' | 'calendar'

const clampMonth = (key: string, months: string[]) => (months.includes(key) ? key : months[0])

const RangeExportModal = ({
  projectId,
  initialFrom,
  initialTo,
  min,
  max,
  onClose,
}: {
  projectId: string
  initialFrom: string
  initialTo: string
  min: string
  max: string
  onClose: () => void
}) => {
  const [from, setFrom] = useState(initialFrom)
  const [to, setTo] = useState(initialTo)
  const { busy, run } = useAction()
  const invalid = !from || !to || to < from

  const exportAs = (format: api.ExportFormat) =>
    run(format, () => api.downloadRange(projectId, from, to, format), t('تم تجهيز الملف المضغوط', 'Zip file is ready')).then(
      (done) => done !== undefined && onClose()
    )

  return (
    <Modal
      title={t('تصدير مجموعة تقارير', 'Export a set of reports')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button icon="download" busy={busy === 'pdf'} disabled={invalid || Boolean(busy)} onClick={() => exportAs('pdf')}>
            PDF (zip)
          </Button>
          <Button
            variant="primary"
            icon="download"
            busy={busy === 'xlsx'}
            disabled={invalid || Boolean(busy)}
            onClick={() => exportAs('xlsx')}
          >
            Excel (zip)
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <TextField label={t('من تاريخ', 'From')} type="date" min={min} max={max} value={from} onChange={(event) => setFrom(event.target.value)} />
        <TextField
          label={t('إلى تاريخ', 'To')}
          type="date"
          min={min}
          max={max}
          value={to}
          onChange={(event) => setTo(event.target.value)}
          error={to && from && to < from ? t('تاريخ النهاية قبل البداية', 'The end date is before the start date') : undefined}
        />
        <p className="field__hint span-2">
          {t(
            'يُنشأ ملف لكل تقرير باسم مثل «DR-001-09-2026 - 2026-09-01» ضمن ملف مضغوط واحد. التقارير غير المعتمدة تحمل علامة «مسودة». قد يستغرق تحويل PDF بعض الوقت.',
            'Each report is saved as its own file, named like “DR-001-09-2026 - 2026-09-01”, inside one zip. Reports that are not approved are marked “Draft”. PDF conversion can take a while.'
          )}
        </p>
      </div>
    </Modal>
  )
}

const ReportsTable = ({ reports, projectId, today }: { reports: ReportListItem[]; projectId: string; today: string }) => {
  const { busy, run } = useAction()
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">{t('رقم التقرير', 'Report no.')}</th>
            <th scope="col">{t('التاريخ', 'Date')}</th>
            <th scope="col">{t('اليوم', 'Day')}</th>
            <th scope="col">{t('الحالة', 'Status')}</th>
            <th scope="col">{t('الاكتمال', 'Completion')}</th>
            <th scope="col">{t('المرفقات', 'Attachments')}</th>
            <th scope="col">{t('أعدّه', 'Prepared by')}</th>
            <th scope="col">
              <span className="sr-only">{t('إجراءات', 'Actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.id} className={report.reportDate === today ? 'is-today' : undefined}>
              <td>
                <Link to={`/projects/${projectId}/reports/${report.id}`} className="code-link">
                  {report.code}
                </Link>
                {report.reportDate === today ? <span className="chip chip--today">{t('اليوم', 'Today')}</span> : null}
              </td>
              <td className="num">{formatDate(report.reportDate)}</td>
              <td>{weekdayFromDate(report.reportDate)}</td>
              <td>
                <StatusBadge status={report.status} />
              </td>
              <td>
                <div
                  className="fill"
                  title={report.filledSections.map((section) => SECTION_LABELS[section]).join(t('، ', ', ')) || t('لا توجد أقسام معبأة', 'No sections filled')}
                >
                  <ProgressBar value={report.fillRatio} label={t(`نسبة الاكتمال ${report.code}`, `Completion for ${report.code}`)} />
                  <span className="num">{report.filledSections.length}/7</span>
                </div>
              </td>
              <td className="num">
                {report.attachmentCount ? (
                  <span className="count">
                    <Icon name="paperclip" size={14} /> {report.attachmentCount}
                  </span>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td>{report.preparedByName ?? <span className="muted">—</span>}</td>
              <td>
                <div className="row-actions">
                  <IconButton
                    icon="download"
                    label={t(`تنزيل ${report.code} Excel`, `Download ${report.code} Excel`)}
                    disabled={busy === `x${report.id}`}
                    onClick={() => run(`x${report.id}`, () => api.downloadReport(report.id, 'xlsx'))}
                  />
                  <IconButton
                    icon="file"
                    label={t(`تنزيل ${report.code} PDF`, `Download ${report.code} PDF`)}
                    disabled={busy === `p${report.id}`}
                    onClick={() => run(`p${report.id}`, () => api.downloadReport(report.id, 'pdf'))}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ReportsCalendar = ({
  reports,
  month,
  projectId,
  today,
}: {
  reports: ReportListItem[]
  month: string
  projectId: string
  today: string
}) => {
  const navigate = useNavigate()
  const byDate = new Map(reports.map((report) => [report.reportDate, report]))
  const { from, to } = monthBounds(month)
  const days: string[] = []
  for (let day = weekStart(from); day <= to || days.length % 7 !== 0; day = addDays(day, 1)) days.push(day)

  return (
    <div className="calendar" role="grid" aria-label={`${t('تقويم', 'Calendar for')} ${monthTitle(month)}`}>
      <div className="calendar__row calendar__row--head" role="row">
        {WEEK_DAYS.map((day, index) => (
          <div key={index} role="columnheader" className={index === 6 ? 'is-weekend' : undefined}>
            {day}
          </div>
        ))}
      </div>
      {Array.from({ length: days.length / 7 }, (_, week) => (
        <div className="calendar__row" role="row" key={week}>
          {days.slice(week * 7, week * 7 + 7).map((day) => {
            const report = byDate.get(day)
            const inMonth = monthKey(day) === month
            if (!report || !inMonth) {
              return (
                <div key={day} role="gridcell" className={inMonth ? 'calendar__cell is-out' : 'calendar__cell is-blank'}>
                  {inMonth ? <span className="calendar__day">{parseIso(day).getUTCDate()}</span> : null}
                </div>
              )
            }
            return (
              <div key={day} role="gridcell" className="calendar__cellwrap">
                <button
                  type="button"
                  className={`calendar__cell calendar__cell--${report.status.toLowerCase()}${day === today ? ' is-today' : ''}`}
                  onClick={() => navigate(`/projects/${projectId}/reports/${report.id}`)}
                  aria-label={`${report.code} ${weekdayFromDate(day)} ${formatDate(day)} - ${STATUS_LABELS[report.status]}`}
                >
                  <span className="calendar__day">{parseIso(day).getUTCDate()}</span>
                  <span className="calendar__code">{codeWithoutMonth(report.code)}</span>
                  <span className="calendar__status">{STATUS_LABELS[report.status]}</span>
                  {report.attachmentCount ? (
                    <span className="calendar__attach">
                      <Icon name="paperclip" size={12} />
                      {report.attachmentCount}
                    </span>
                  ) : null}
                </button>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export const ProjectIndexPage = () => {
  const { locale } = useRegion()
  const { project } = useProject()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const months = useMemo(() => monthsBetween(project.startDate, project.endDate), [project.startDate, project.endDate])
  const { data: today } = useAsync(api.fetchToday, [])

  const defaultMonth = today ? clampMonth(monthKey(today), months) : months[0]
  const month = clampMonth(params.get('month') ?? defaultMonth, months)
  const view: View = params.get('view') === 'calendar' ? 'calendar' : 'table'
  const statusFilter = (params.get('status') as DisplayStatus | null) ?? null
  const [search, setSearch] = useState('')
  const [rangeOpen, setRangeOpen] = useState(false)
  const { busy, run } = useAction()

  const { from, to } = monthBounds(month)
  const { data: reports, error, loading, reload } = useAsync(
    () => api.fetchReports(project.id, from, to),
    [project.id, from, to]
  )

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)))
    setParams(next, { replace: true })
  }

  const counts = useMemo(() => {
    const result = Object.fromEntries(STATUS_ORDER.map((status) => [status, 0])) as Record<DisplayStatus, number>
    reports?.forEach((report) => (result[report.status] += 1))
    return result
  }, [reports])

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (reports ?? []).filter((report) => {
      const day = (locale === 'en' ? weekdayFromDate(report.reportDate) : report.dayName).toLowerCase()
      return (
        (!statusFilter || report.status === statusFilter) &&
        (!term ||
          report.code.toLowerCase().includes(term) ||
          report.reportDate.includes(term) ||
          formatDate(report.reportDate).includes(term) ||
          report.dayName.toLowerCase().includes(term) ||
          day.includes(term))
      )
    })
  }, [reports, search, statusFilter, locale])

  const monthIndex = months.indexOf(month)
  const todayInProject = today && today >= project.startDate && today <= project.endDate && project.myRole !== 'SUPERVISOR'

  const openToday = async () => {
    if (!today) return
    const id = await run('today', () => api.findReportByDate(project.id, today))
    if (id) navigate(`/projects/${project.id}/reports/${id}`)
  }

  const exportMonth = (format: api.ExportFormat) =>
    run(`month-${format}`, () => api.downloadRange(project.id, from, to, format), t('تم تجهيز ملف الشهر', 'Month file is ready'))

  return (
    <div className="page">
      <h1>{t('فهرس التقارير', 'Report index')}</h1>
      <div className="page__head">
        <div className="month-switch" role="group" aria-label={t('اختيار الشهر', 'Choose month')}>
          <IconButton
            icon="chevronRight"
            label={t('الشهر السابق', 'Previous month')}
            disabled={monthIndex <= 0}
            onClick={() => update({ month: months[monthIndex - 1] })}
          />
          <label className="sr-only" htmlFor="month-select">
            {t('الشهر', 'Month')}
          </label>
          <select id="month-select" className="month-switch__select" value={month} onChange={(event) => update({ month: event.target.value })}>
            {months.map((key) => (
              <option key={key} value={key}>
                {monthTitle(key)}
              </option>
            ))}
          </select>
          <IconButton
            icon="chevronLeft"
            label={t('الشهر التالي', 'Next month')}
            disabled={monthIndex >= months.length - 1}
            onClick={() => update({ month: months[monthIndex + 1] })}
          />
        </div>
        <div className="toolbar">
          {todayInProject ? (
            <Button variant="primary" icon="edit" busy={busy === 'today'} onClick={openToday}>
              {t('تقرير اليوم', "Today's report")}
            </Button>
          ) : null}
          <Button icon="download" busy={busy === 'month-xlsx'} onClick={() => exportMonth('xlsx')}>
            {t('Excel للشهر', 'Excel for the month')}
          </Button>
          <Button icon="file" busy={busy === 'month-pdf'} onClick={() => exportMonth('pdf')}>
            {t('PDF للشهر', 'PDF for the month')}
          </Button>
          <Button icon="calendar" onClick={() => setRangeOpen(true)}>
            {t('تصدير فترة', 'Export a period')}
          </Button>
        </div>
      </div>

      <div className="stats" role="group" aria-label={t('تصفية حسب الحالة', 'Filter by status')}>
        <button
          type="button"
          className={statusFilter ? 'stat' : 'stat is-active'}
          aria-pressed={!statusFilter}
          onClick={() => update({ status: null })}
        >
          <span className="stat__value">{reports?.length ?? '—'}</span>
          <span className="stat__label">{t('كل التقارير', 'All reports')}</span>
        </button>
        {STATUS_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            className={`stat stat--${status.toLowerCase()}${statusFilter === status ? ' is-active' : ''}`}
            aria-pressed={statusFilter === status}
            onClick={() => update({ status: statusFilter === status ? null : status })}
          >
            <span className="stat__value">{reports ? counts[status] : '—'}</span>
            <span className="stat__label">{STATUS_LABELS[status]}</span>
          </button>
        ))}
      </div>

      <Card
        title={`${t('تقارير', 'Reports for')} ${monthTitle(month)}`}
        actions={
          <>
            <div className="search">
              <Icon name="search" size={16} />
              <label htmlFor="report-search" className="sr-only">
                {t('بحث', 'Search')}
              </label>
              <input
                id="report-search"
                type="search"
                placeholder={t('بحث برقم التقرير أو التاريخ', 'Search by report number or date')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="segmented" role="group" aria-label={t('طريقة العرض', 'View')}>
              <button type="button" aria-pressed={view === 'table'} onClick={() => update({ view: null })}>
                <Icon name="list" /> {t('جدول', 'Table')}
              </button>
              <button type="button" aria-pressed={view === 'calendar'} onClick={() => update({ view: 'calendar' })}>
                <Icon name="calendar" /> {t('تقويم', 'Calendar')}
              </button>
            </div>
          </>
        }
      >
        {loading && !reports ? (
          <Spinner />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : view === 'calendar' ? (
          <ReportsCalendar reports={visible} month={month} projectId={project.id} today={today ?? ''} />
        ) : visible.length ? (
          <ReportsTable reports={visible} projectId={project.id} today={today ?? ''} />
        ) : (
          <EmptyState title={t('لا توجد تقارير مطابقة', 'No matching reports')} icon="list" />
        )}
      </Card>

      {rangeOpen ? (
        <RangeExportModal
          projectId={project.id}
          initialFrom={from}
          initialTo={to}
          min={project.startDate}
          max={project.endDate}
          onClose={() => setRangeOpen(false)}
        />
      ) : null}
    </div>
  )
}
